import { jsx as _jsx } from "react/jsx-runtime";

let _ = t => t,
    _t;

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { useSelector } from 'react-redux';
import { MONTHLY } from 'self-service-api/constants/Terms';
import styled, { css } from 'styled-components';
import Small from 'UIComponents/elements/Small';
import breakpoint from '../../../constants/Breakpoints';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { isEligibleForLatamPromotionSelector, isEligibleForRetailPromotionSelector } from '../../../selectors/PromotionSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import PriceDisplay from './PriceDisplay';
const fullPriceStyles = css(["line-height:initial;padding:1px 0 2px 0;display:block;"]);
export const FullPriceContainerBlock = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__FullPriceContainerBlock",
  componentId: "zvi2o2-0"
})(["font-size:1.25rem;font-weight:500;", ""], fullPriceStyles);
export const FullPriceContainerBlockLarge = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__FullPriceContainerBlockLarge",
  componentId: "zvi2o2-1"
})(["font-size:1.375rem;font-weight:500;margin-bottom:0;", ""], fullPriceStyles);
export const PriceContainer = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__PriceContainer",
  componentId: "zvi2o2-2"
})(["line-height:initial;"]);
export const CrossedOutPrice = styled(PriceDisplay).withConfig({
  displayName: "HighlightedPriceDisplay__CrossedOutPrice",
  componentId: "zvi2o2-3"
})(["text-decoration:line-through;"]);
export const CrossedOutSubtextPrice = styled(PriceDisplay).withConfig({
  displayName: "HighlightedPriceDisplay__CrossedOutSubtextPrice",
  componentId: "zvi2o2-4"
})(["text-decoration:line-through;"]);
export const PriceSection = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__PriceSection",
  componentId: "zvi2o2-5"
})(["text-align:left;"]);
export const MicroCopy = styled(Small).withConfig({
  displayName: "HighlightedPriceDisplay__MicroCopy",
  componentId: "zvi2o2-6"
})(["display:block;", ""], breakpoint.tablet(_t || (_t = _`
    display: inline;
  `)));
export const Bold = styled.span.withConfig({
  displayName: "HighlightedPriceDisplay__Bold",
  componentId: "zvi2o2-7"
})(["font-weight:600;"]);
export const PerMonth = styled.span.withConfig({
  displayName: "HighlightedPriceDisplay__PerMonth",
  componentId: "zvi2o2-8"
})(["font-weight:500;"]);
export const HighlightedPriceDisplay = ({
  pricingInfo,
  skuId,
  nonMerchandiseIdProduct,
  wrapperStyle,
  useLargePrice,
  showingOnlyAdditionalCost = false,
  concise = false
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const showRetailStarterPrice = useSelector(state => skuId && isEligibleForRetailPromotionSelector(state, {
    skuId
  }));
  const currencyCode = useSelector(activeCurrencySelector);
  const {
    monthly: {
      fullPrice: monthlyFullPrice,
      calculatedPrice: monthlyCalculatedPrice
    },
    annually: {
      fullPrice: annualFullPrice,
      calculatedPrice: annualCalculatedPrice
    },
    offerInfo
  } = pricingInfo; // Temporary solution - only show for latam

  const showCrossedOutMonthlyFullPrice = useSelector(state => skuId && isEligibleForLatamPromotionSelector(state, {
    skuId
  }));
  let discountedKey = offerInfo && offerInfo.payment === MONTHLY ? showCrossedOutMonthlyFullPrice ? 'promotionPricingPerMonthBilledMonthly_jsx' : 'discountedPricingPerMonthBilledMonthly_jsx' : showCrossedOutMonthlyFullPrice ? 'promotionPricingPerYearAtCost_jsx' : 'discountedPricingPerYearAtCost_jsx';
  let normalKey = offerInfo && offerInfo.payment === MONTHLY ? showingOnlyAdditionalCost ? 'additionalCostPerMonthBilledMonthly_jsx' : 'pricingPerMonthBilledMonthly_jsx' : showingOnlyAdditionalCost ? 'additionalCostPerMonthBilledYearly_jsx' : 'pricingPerYearAtCost_jsx';

  if (isSeatsRedesignUngated) {
    discountedKey = offerInfo && showCrossedOutMonthlyFullPrice ? 'promotion_jsx' : 'discounted_jsx';
    normalKey = offerInfo && showingOnlyAdditionalCost ? 'additional_jsx' : 'startsAt_jsx';
  }

  const shouldCheckOfferInfoForDiscount = !!skuId && !showRetailStarterPrice;
  const showDiscountedCopy = !showingOnlyAdditionalCost && (shouldCheckOfferInfoForDiscount ? offerInfo && offerInfo.discount > 0 : monthlyFullPrice > monthlyCalculatedPrice); // Temporary for CRM suite where we override the price object

  const apiName = useSelector(state => {
    const product = skuId && productForSkuIdSelector(state, {
      skuId
    });
    return product && product.apiName;
  });
  const seleniumId = apiName || nonMerchandiseIdProduct || 'loading';
  if (concise) return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `merchCards.pricing.concise_jsx`,
    options: {
      currencyCode,
      price: showDiscountedCopy ? monthlyCalculatedPrice : monthlyFullPrice,
      seleniumId
    },
    elements: {
      PriceDisplay
    }
  });
  return /*#__PURE__*/_jsx(PriceSection, {
    style: wrapperStyle,
    children: showDiscountedCopy ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `merchCards.pricing.${discountedKey}`,
      options: {
        currencyCode,
        monthlyFullPrice,
        monthlyDiscountedPrice: monthlyCalculatedPrice,
        seleniumId,
        annualFullPrice,
        annualDiscountedPrice: annualCalculatedPrice
      },
      elements: {
        MicroCopy,
        FullPriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
        CrossedOutSubtextPrice,
        PriceDisplay,
        Bold,
        PriceContainer,
        PerMonth
      }
    }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `merchCards.pricing.${normalKey}`,
      options: {
        currencyCode,
        price: monthlyCalculatedPrice,
        seleniumId,
        annualPrice: annualCalculatedPrice
      },
      elements: {
        MicroCopy,
        PriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
        PriceDisplay,
        Bold,
        PerMonth
      }
    })
  });
};